import React, { useCallback, useEffect, useState } from 'react';
import {
  Wallet,
  Key,
  CreditCard,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Coins,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import { walletService } from '../../services/walletService';
import type { Transaction } from '../../types/wallet';
import { cardTheme } from '../../styles/cardTheme';
import {
  formatCoins,
  formatCoinsDelta,
  formatTransactionDescription,
  formatWalletDate,
  getTransactionTypeLabel,
  isValidWalletCode,
} from '../../utils/walletLabels';

const CHARGE_PRESETS = [50, 100, 200, 500];

const PortalWallet: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [walletCode, setWalletCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [showRedeemSuccess, setShowRedeemSuccess] = useState(false);
  const [addedCoins, setAddedCoins] = useState(0);
  const [newBalanceAfterRedeem, setNewBalanceAfterRedeem] = useState(0);

  const [chargeAmount, setChargeAmount] = useState('');
  const [isCharging, setIsCharging] = useState(false);
  const [chargeError, setChargeError] = useState<string | null>(null);

  const loadWallet = useCallback(async (page = 1) => {
    setIsLoading(true);
    setLoadError(null);
    const result = await walletService.getMyHistory(page, 10);
    if (result.success) {
      setBalance(result.data.history.currentBalance);
      setTransactions(result.data.history.transactions);
      setPageNumber(result.data.pageNumber);
      setTotalPages(result.data.totalPages);
    } else {
      setLoadError(result.error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadWallet(1);
  }, [loadWallet]);

  useEffect(() => {
    if (!showRedeemSuccess) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowRedeemSuccess(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showRedeemSuccess]);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setRedeemError(null);

    const code = walletCode.trim();
    if (!isValidWalletCode(code)) {
      setRedeemError('كود المحفظة يجب أن يكون 7 أرقام');
      return;
    }

    setIsRedeeming(true);
    const result = await walletService.redeemWalletCode(code);
    setIsRedeeming(false);

    if (result.success) {
      setBalance(result.data.newBalance);
      setAddedCoins(result.data.addedCoins);
      setNewBalanceAfterRedeem(result.data.newBalance);
      setWalletCode('');
      setShowRedeemSuccess(true);
      loadWallet(1);
    } else {
      setRedeemError(result.error);
    }
  };

  const handleCharge = async (amount: number) => {
    if (amount <= 0) {
      setChargeError('يرجى إدخال مبلغ صحيح');
      return;
    }

    setChargeError(null);
    setIsCharging(true);
    const result = await walletService.chargeWallet(amount);
    setIsCharging(false);

    if (result.success && result.data.paymentSession.sessionUrl) {
      window.location.href = result.data.paymentSession.sessionUrl;
    } else {
      setChargeError(result.success ? 'لم يتم العثور على رابط الدفع' : result.error);
    }
  };

  const handleCustomCharge = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(chargeAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setChargeError('يرجى إدخال مبلغ صحيح');
      return;
    }
    void handleCharge(amount);
  };

  return (
    <div className="relative overflow-x-hidden px-4 pb-8 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8" dir="rtl">
      {showRedeemSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          dir="rtl"
          onClick={() => setShowRedeemSuccess(false)}
          role="presentation"
        >
          <div
            className={`${cardTheme.shell} relative mx-4 w-full max-w-sm p-6 shadow-xl`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wallet-redeem-success-title"
          >
            <button
              type="button"
              onClick={() => setShowRedeemSuccess(false)}
              className="absolute left-3 top-3 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-400 hover:bg-green-50 hover:text-green-800"
              aria-label="إغلاق"
            >
              <X size={18} />
            </button>
            <div className="pt-2 text-center">
              <div
                className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${cardTheme.iconBox}`}
              >
                <CheckCircle className="h-8 w-8" />
              </div>
              <h3
                id="wallet-redeem-success-title"
                className={`mb-2 text-xl font-bold ${cardTheme.title}`}
              >
                تمام كده!
              </h3>
              <p className={`mb-1 text-sm leading-relaxed ${cardTheme.subtitle}`}>
                الكود اتشحن بنجاح
              </p>
              <p className="text-sm leading-relaxed text-gray-500">
                اتضاف{' '}
                <span className="font-semibold text-green-800">
                  {formatCoins(addedCoins)} عملة
                </span>{' '}
                لمحفظتك
              </p>
              <p className="mt-3 rounded-xl border border-green-100 bg-green-50/70 px-3 py-2.5 text-sm text-green-800">
                رصيدك دلوقتي:{' '}
                <span className="font-bold font-cairo-semibold">
                  {formatCoins(newBalanceAfterRedeem)} عملة
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowRedeemSuccess(false)}
              className={`mt-6 flex w-full min-h-12 items-center justify-center rounded-xl px-4 text-sm font-semibold ${cardTheme.btnPrimary}`}
            >
              تمام
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-5 sm:mb-8">
        <span className={`inline-flex items-center gap-2 mb-3 ${cardTheme.badgeGold}`}>
          <Wallet className="w-4 h-4" />
          محفظتي
        </span>
        <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold mb-1.5 ${cardTheme.title}`}>
          رصيدك ومعاملاتك
        </h1>
        <p className={`text-sm sm:text-base ${cardTheme.subtitle}`}>
          شحن المحفظة، استخدام الأكواد، وشراء الكورسات
        </p>
      </div>

      {/* Balance card */}
      <div
        className={`mb-5 sm:mb-8 rounded-2xl ${cardTheme.headerBanner} p-4 sm:p-6 lg:p-8 text-white shadow-lg shadow-green-900/20`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-green-100/90 text-xs sm:text-sm mb-1">الرصيد الحالي</p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <Coins className="w-6 h-6 sm:w-7 sm:h-7 text-gold-300 shrink-0" />
              {isLoading ? (
                <div className="h-9 sm:h-10 w-24 bg-white/20 rounded-lg animate-pulse" />
              ) : (
                <span className="text-3xl sm:text-4xl lg:text-5xl font-bold font-cairo-bold break-all">
                  {formatCoins(balance ?? 0)}
                </span>
              )}
              <span className="text-green-100 text-base sm:text-lg">عملة</span>
            </div>
            <p className="text-green-100/75 text-xs mt-2">1 عملة = 1 جنيه مصري</p>
          </div>
          <button
            type="button"
            onClick={() => loadWallet(pageNumber)}
            disabled={isLoading}
            className="shrink-0 p-2.5 min-h-11 min-w-11 rounded-xl bg-white/15 hover:bg-white/25 transition-colors disabled:opacity-50"
            aria-label="تحديث الرصيد"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mb-5 flex items-start gap-2 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-5 sm:mb-8">
        {/* Redeem wallet code */}
        <div className={`${cardTheme.shell} p-4 sm:p-6`}>
          <h2 className={`text-base sm:text-lg font-bold mb-1 flex items-center gap-2 ${cardTheme.title}`}>
            <Key className="w-5 h-5 text-gold-600 shrink-0" />
            شحن بكود المحفظة
          </h2>
          <p className={`text-xs sm:text-sm mb-4 ${cardTheme.subtitle}`}>
            أدخل كود المحفظة المكوّن من 7 أرقام
          </p>

          <form onSubmit={handleRedeem} className="space-y-3">
            <input
              type="text"
              inputMode="numeric"
              maxLength={7}
              value={walletCode}
              onChange={(e) => {
                setWalletCode(e.target.value.replace(/\D/g, '').slice(0, 7));
                setRedeemError(null);
              }}
              placeholder="0000000"
              className="w-full min-h-12 px-3 sm:px-4 py-3 border-2 border-green-100 rounded-xl text-center text-lg sm:text-xl tracking-[0.25em] sm:tracking-[0.3em] font-mono focus:outline-none focus:border-gold-400"
              disabled={isRedeeming}
              autoComplete="off"
            />
            {redeemError && (
              <p className="text-sm text-red-600 flex items-start gap-1.5">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {redeemError}
              </p>
            )}
            <button
              type="submit"
              disabled={isRedeeming || walletCode.length !== 7}
              className={`w-full min-h-11 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${cardTheme.btnGold}`}
            >
              {isRedeeming ? 'جاري التحقق...' : 'استخدام الكود'}
            </button>
          </form>
        </div>

        {/* Charge via payment gateway */}
        <div className={`${cardTheme.shell} p-4 sm:p-6`}>
          <h2 className={`text-base sm:text-lg font-bold mb-1 flex items-center gap-2 ${cardTheme.title}`}>
            <CreditCard className="w-5 h-5 text-green-700 shrink-0" />
            شحن عبر بوابة الدفع
          </h2>
          <p className={`text-xs sm:text-sm mb-4 ${cardTheme.subtitle}`}>
            ادفع بالبطاقة أو المحفظة الإلكترونية لإضافة عملات
          </p>

          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
            {CHARGE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => void handleCharge(preset)}
                disabled={isCharging}
                className={`min-h-11 py-2.5 px-2 sm:px-4 border-2 border-green-100 rounded-xl text-sm sm:text-base font-semibold text-green-800 hover:border-gold-400 hover:bg-gold-50/50 transition-all disabled:opacity-50`}
              >
                {formatCoins(preset)} جنيه
              </button>
            ))}
          </div>

          <form onSubmit={handleCustomCharge} className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <input
              type="number"
              min={1}
              inputMode="numeric"
              value={chargeAmount}
              onChange={(e) => {
                setChargeAmount(e.target.value);
                setChargeError(null);
              }}
              placeholder="مبلغ مخصص"
              className="w-full min-h-11 flex-1 px-3 sm:px-4 py-2.5 border-2 border-green-100 rounded-xl focus:outline-none focus:border-gold-400"
              disabled={isCharging}
            />
            <button
              type="submit"
              disabled={isCharging || !chargeAmount}
              className={`w-full sm:w-auto sm:min-w-[5.5rem] min-h-11 px-5 py-2.5 rounded-xl font-semibold transition-colors disabled:opacity-50 ${cardTheme.btnPrimary}`}
            >
              {isCharging ? '...' : 'شحن'}
            </button>
          </form>
          {chargeError && (
            <p className="mt-2 text-sm text-red-600 flex items-start gap-1.5">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {chargeError}
            </p>
          )}
        </div>
      </div>

      {/* Transaction history */}
      <div className={`${cardTheme.shell}`}>
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-green-100">
          <h2 className={`text-base sm:text-lg font-bold ${cardTheme.title}`}>سجل المعاملات</h2>
        </div>

        {isLoading ? (
          <div className="p-6 sm:p-8 text-center text-gray-500 text-sm">جاري التحميل...</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 sm:p-12 text-center text-gray-500">
            <Wallet className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm sm:text-base">لا توجد معاملات بعد</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-green-50">
              {transactions.map((tx) => {
                const isCredit = tx.amount > 0;
                return (
                  <div
                    key={tx.id}
                    className="px-4 sm:px-6 py-3 sm:py-4 hover:bg-green-50/30 transition-colors"
                  >
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-4">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                            isCredit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {isCredit ? (
                            <ArrowDownLeft className="w-5 h-5" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">
                            {formatTransactionDescription(tx.description, tx.type)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                            {getTransactionTypeLabel(tx.type)}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5 sm:hidden">
                            {formatWalletDate(tx.createdAt)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-3 ps-[3.25rem] sm:ps-0 sm:shrink-0 sm:flex-col sm:items-end sm:gap-0.5 border-t border-green-50 pt-2.5 sm:border-0 sm:pt-0">
                        <p
                          className={`font-bold text-base sm:text-sm ${
                            isCredit ? 'text-green-700' : 'text-red-600'
                          }`}
                        >
                          {formatCoinsDelta(tx.amount)} عملة
                        </p>
                        <p className="text-xs text-gray-400">
                          الرصيد: {formatCoins(tx.balanceAfter)}
                        </p>
                        <p className="hidden sm:block text-xs text-gray-400 mt-0.5">
                          {formatWalletDate(tx.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col gap-3 border-t border-green-100 px-4 py-3 pb-20 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
                <span className="text-sm text-gray-500 text-center sm:order-2">
                  صفحة {pageNumber} من {totalPages}
                </span>
                <div className="grid grid-cols-2 gap-2 sm:contents">
                  <button
                    type="button"
                    onClick={() => loadWallet(pageNumber - 1)}
                    disabled={pageNumber <= 1 || isLoading}
                    className="flex items-center justify-center gap-1 min-h-11 px-3 py-2 text-sm font-medium text-green-800 border border-green-200 rounded-xl hover:bg-green-50 disabled:opacity-40 sm:order-1"
                  >
                    <ChevronRight className="w-4 h-4" />
                    السابق
                  </button>
                  <button
                    type="button"
                    onClick={() => loadWallet(pageNumber + 1)}
                    disabled={pageNumber >= totalPages || isLoading}
                    className="flex items-center justify-center gap-1 min-h-11 px-3 py-2 text-sm font-medium text-green-800 border border-green-200 rounded-xl hover:bg-green-50 disabled:opacity-40 sm:order-3"
                  >
                    التالي
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PortalWallet;
